/* eslint-disable react/jsx-pascal-case */
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import { useTheme } from "@mui/material/styles";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { PageHeader } from "../ui";
import {
  getStrategyRegionsColumns,
  getMissingRegionsColumns,
} from "../table/columnsConfig/strategyRegionsColumns";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";
import ExportToCSVButton from "../ExportToCsvButton";
import {
  createStrategyRegions,
  updateStrategyRegions,
  deleteStrategyRegions,
} from "../../services/Axios";

const columnsToExport = ["StrategyName", "Region", "id", "actions"];
const missingRegionsColumnsToExport = ["StrategyName", "addRegion"];

const StrategyRegions = () => {
  const theme = useTheme();
  const { datasets, isLoading, error } = useContext(PositionsDataContext);
  const [allData, setAllData] = useState([]);
  const [strategyRegionsData, setStrategyRegionsData] = useState([]);
  const [strategyAllowedRegionsData, setStrategyAllowedRegionsData] = useState(
    []
  );
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [editingStrategyWithRegion, setEditingStrategyWithRegion] =
    useState(null);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    strategyName: "",
    strategyId: null,
  });
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State for left table (strategies with regions)
  const [strategiesWithRegions, setStrategiesWithRegions] = useState([]);
  const [
    strategiesWithRegionsGlobalFilter,
    setStrategiesWithRegionsGlobalFilter,
  ] = useState("");

  // State for right table (strategies missing from regions)
  const [strategiesMissingRegions, setStrategiesMissingRegions] = useState([]);
  const [
    strategiesMissingRegionsGlobalFilter,
    setStrategiesMissingRegionsGlobalFilter,
  ] = useState("");

  // Effects
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.strategyRegionsData)
      setStrategyRegionsData(datasets.strategyRegionsData);
    if (datasets?.strategyAllowedRegionsData)
      setStrategyAllowedRegionsData(datasets.strategyAllowedRegionsData);
  }, [datasets]);

  // Process data to separate strategies with regions vs missing regions
  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for only "merger arb" strategies and get unique StrategyNames
      const mergerArbStrategies = allData
        .filter((position) => {
          const strategyType = position.StrategyType;
          return strategyType && strategyType === "Merger Arbitrage";
        })
        .map((position) => position.StrategyName)
        .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates

      if (strategyRegionsData?.length > 0) {
        // Get all strategy names that have strategy regions data
        const strategiesWithRegions = new Set(
          strategyRegionsData.map((item) => item.StrategyName)
        );

        // Left table: strategies that have regions
        const withRegions = mergerArbStrategies
          .filter((strategyName) => {
            // Check if this strategy name exists in the strategy regions data
            const hasRegion = strategiesWithRegions.has(strategyName);
            return hasRegion;
          })
          .map((strategyName) => {
            // Find the region data for this strategy
            const regionData = strategyRegionsData.find(
              (item) => item.StrategyName === strategyName
            );
            return {
              StrategyName: strategyName,
              Region: regionData?.Region || "No Region",
              id: regionData?.id || null, // Include ID for delete functionality
            };
          });
        setStrategiesWithRegions(withRegions);

        // Right table: strategies missing from regions
        const missingRegions = mergerArbStrategies.filter((strategyName) => {
          // Check if this strategy name is missing from the strategy regions data
          const hasRegion = strategiesWithRegions.has(strategyName);

          return !hasRegion;
        });
        setStrategiesMissingRegions(missingRegions);
      } else {
        // If no strategy regions data, all strategies go to missing regions
        setStrategiesWithRegions([]);
        setStrategiesMissingRegions(mergerArbStrategies);
      }
    } else {
      setStrategiesWithRegions([]);
      setStrategiesMissingRegions([]);
    }
  }, [allData, strategyRegionsData]);

  // Function to handle adding a region to a strategy
  const handleAddRegion = async (strategyName, region) => {
    try {
      // Create the new strategy region entry
      const newStrategyRegion = {
        StrategyName: strategyName,
        Region: region,
        // Add any other required fields based on your API
      };

      // Call the API to create the strategy region
      const response = await createStrategyRegions(newStrategyRegion);

      // Show success message
      setMessage({
        type: "success",
        text: `Region "${region}" added to strategy "${strategyName}" successfully!`,
      });

      // Update the local state to move the strategy to the left table
      setStrategiesMissingRegions((prev) =>
        prev.filter((name) => name !== strategyName)
      );

      // Add the strategy to the left table with proper structure
      const newStrategyWithRegion = {
        StrategyName: strategyName,
        Region: region,
        id: response?.id || null, // Use the ID from the API response if available
      };
      setStrategiesWithRegions((prev) => [...prev, newStrategyWithRegion]);

      // Reset editing state
      setEditingStrategy(null);
      setSelectedRegion("");

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);

      // Optionally refresh the data from the server
      // You might want to call a refresh function here
    } catch (error) {
      console.error("Error adding region to strategy:", error);
      // Show error message
      setMessage({
        type: "error",
        text: `Failed to add region: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Function to start editing a strategy
  const handleStartEdit = (strategyName) => {
    setEditingStrategy(strategyName);
    setSelectedRegion("");
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingStrategy(null);
    setEditingStrategyWithRegion(null);
    setSelectedRegion("");
  };

  // Function to start editing a strategy with region
  const handleStartEditWithRegion = (strategyName, currentRegion) => {
    setEditingStrategyWithRegion(strategyName);
    setSelectedRegion(currentRegion || "");
  };

  // Function to handle delete confirmation
  const handleDeleteClick = (strategyName, strategyId) => {
    setDeleteConfirmation({ open: true, strategyName, strategyId });
  };

  // Function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      const { strategyName, strategyId } = deleteConfirmation;

      // Call the API to delete the strategy region
      await deleteStrategyRegions(strategyId);

      // Show success message
      setMessage({
        type: "success",
        text: `Strategy "${strategyName}" deleted successfully!`,
      });

      // Remove from local state
      setStrategiesWithRegions((prev) =>
        prev.filter((strategy) => strategy.StrategyName !== strategyName)
      );

      // Close confirmation dialog
      setDeleteConfirmation({
        open: false,
        strategyName: "",
        strategyId: null,
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error deleting strategy region:", error);
      setMessage({
        type: "error",
        text: `Failed to delete strategy: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Function to update region for existing strategy
  const handleUpdateRegion = async (strategyName, newRegion) => {
    try {
      // Find the existing strategy region entry to get the ID
      const existingStrategy = strategyRegionsData.find(
        (item) => item.StrategyName === strategyName
      );

      if (!existingStrategy || !existingStrategy.id) {
        throw new Error("Strategy not found or missing ID");
      }

      // Update the strategy region entry
      const updatedStrategyRegion = {
        StrategyName: strategyName,
        Region: newRegion,
      };

      // Call the API to update the strategy region
      await updateStrategyRegions(existingStrategy.id, updatedStrategyRegion);

      // Show success message
      setMessage({
        type: "success",
        text: `Region updated to "${newRegion}" for strategy "${strategyName}" successfully!`,
      });

      // Update the local state
      setStrategiesWithRegions((prev) =>
        prev.map((strategy) =>
          strategy.StrategyName === strategyName
            ? { ...strategy, Region: newRegion }
            : strategy
        )
      );

      // Reset editing state
      setEditingStrategyWithRegion(null);
      setSelectedRegion("");

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error updating region for strategy:", error);
      // Show error message
      setMessage({
        type: "error",
        text: `Failed to update region: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Left table configuration
  const strategiesWithRegionsTable = useMaterialReactTable({
    columns: getStrategyRegionsColumns(theme, {
      editingStrategy: editingStrategyWithRegion,
      selectedRegion,
      strategyAllowedRegionsData,
      onStartEdit: handleStartEditWithRegion,
      onCancelEdit: handleCancelEdit,
      onUpdateRegion: handleUpdateRegion,
      onDeleteClick: handleDeleteClick,
      onRegionChange: setSelectedRegion,
    }),
    data: strategiesWithRegions,
    enableColumnDragging: false,
    enableGrouping: false,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    initialState: {
      sorting: [{ id: "StrategyName", desc: false }],
      density: "compact",
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: strategiesWithRegionsGlobalFilter,
    },
    state: {
      globalFilter: strategiesWithRegionsGlobalFilter,
    },
    onGlobalFilterChange: setStrategiesWithRegionsGlobalFilter,
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "400px",
          sm: "500px",
          md: "calc(100vh - 250px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Right table configuration
  const strategiesMissingRegionsTable = useMaterialReactTable({
    columns: getMissingRegionsColumns(theme, {
      editingStrategy,
      selectedRegion,
      strategyAllowedRegionsData,
      onStartEdit: handleStartEdit,
      onCancelEdit: handleCancelEdit,
      onAddRegion: handleAddRegion,
      onRegionChange: setSelectedRegion,
    }),
    data: strategiesMissingRegions.map((name) => ({ StrategyName: name })),

    enableColumnDragging: false,
    enableGrouping: false,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    initialState: {
      sorting: [{ id: "StrategyName", desc: false }],
      density: "compact",
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: strategiesMissingRegionsGlobalFilter,
    },
    state: {
      globalFilter: strategiesMissingRegionsGlobalFilter,
    },
    onGlobalFilterChange: setStrategiesMissingRegionsGlobalFilter,
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "400px",
          sm: "500px",
          md: "calc(100vh - 250px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading strategy regions data...</div>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Error loading data: {error}</div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Message Display */}
      {message.text && (
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            backgroundColor:
              message.type === "success"
                ? theme.palette.success.light
                : theme.palette.error.light,
            color:
              message.type === "success"
                ? theme.palette.success.contrastText
                : theme.palette.error.contrastText,
            borderRadius: 1,
            border: `1px solid ${
              message.type === "success"
                ? theme.palette.success.main
                : theme.palette.error.main
            }`,
          }}
        >
          {message.text}
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          display: "flex",
          gap: { xs: 1, sm: 2, md: 3 },
          padding: { xs: 0.5, sm: 1 },
          height: "calc(100vh - 120px)",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Section - Strategies with Regions */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            boxShadow: theme.shadows[1],
          }}
        >
          <PageHeader
            sx={{
              px: "16px",
              py: "12px",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
            title={"Strategies with Regions"}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Search Field */}
                <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                  <MRT_GlobalFilterTextField
                    table={strategiesWithRegionsTable}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                        },
                        "&.Mui-focused": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: theme.spacing(0.75, 1.25),
                        color: theme.palette.text.primary,
                        "&::placeholder": {
                          color: theme.palette.text.secondary,
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <ExportToCSVButton
                  columns={columnsToExport}
                  theme={theme}
                  globalFilter=""
                  formatCurrency={() => {}}
                  formatNumber={() => {}}
                  data={strategiesWithRegions.map((strategy) => ({
                    ...strategy,
                    actions: "Edit/Delete Buttons",
                  }))}
                  filename={"Merger Arb Strategies with Regions"}
                />
              </Box>
            }
          />
          <Box
            style={{
              flex: 1,
              overflow: "hidden",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <MaterialReactTable table={strategiesWithRegionsTable} />
          </Box>
        </Box>

        {/* Right Section - Strategies Missing Regions */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "auto",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            boxShadow: theme.shadows[1],
          }}
        >
          <PageHeader
            sx={{
              px: "16px",
              py: "12px",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
            title={"Strategies Missing Regions"}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Search Field */}
                <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                  <MRT_GlobalFilterTextField
                    table={strategiesMissingRegionsTable}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                        },
                        "&.Mui-focused": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: theme.spacing(0.75, 1.25),
                        color: theme.palette.text.primary,
                        "&::placeholder": {
                          color: theme.palette.text.secondary,
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <ExportToCSVButton
                  columns={missingRegionsColumnsToExport}
                  theme={theme}
                  globalFilter=""
                  formatCurrency={() => {}}
                  formatNumber={() => {}}
                  data={strategiesMissingRegions.map((name) => ({
                    StrategyName: name,
                    addRegion: "Add Region Button",
                  }))}
                  filename={"Merger Arb Strategies Missing Regions"}
                />
              </Box>
            }
          />
          <Box
            style={{
              flex: 1,
              overflow: "hidden",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <MaterialReactTable table={strategiesMissingRegionsTable} />
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              padding: 3,
              maxWidth: "400px",
              width: "90%",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Confirm Delete
            </Box>
            <Box
              sx={{
                fontSize: "1rem",
                color: theme.palette.text.secondary,
                mb: 3,
              }}
            >
              Are you sure you want to delete the strategy region for{" "}
              <strong>"{deleteConfirmation.strategyName}"</strong>? This action
              cannot be undone.
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <button
                onClick={() =>
                  setDeleteConfirmation({
                    open: false,
                    strategyName: "",
                    strategyId: null,
                  })
                }
                style={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  backgroundColor: theme.palette.grey[500],
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  backgroundColor: theme.palette.error.main,
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StrategyRegions;
